import { api } from "../../../api/apiSlice";

// Branch-wise master (handoff §9.8) - same shape as Customers: each row
// belongs to exactly one branch, Product_Code is only branch-local (2,087 of
// 3,288 collide across branches), so every single-product route needs
// {branchId} alongside {productCode} to disambiguate. salesRate is computed
// server-side (purchaseRate + purchaseRate * markupPercentage / 100) - never
// sent, always returned.
export const productsApi = api.injectEndpoints({
  endpoints: (builder) => ({
    getProducts: builder.query({
      query: (params) => ({ url: "/masters/products", params }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((p) => ({ type: "MasterProducts", id: p.productCode })),
              { type: "MasterProducts", id: "LIST" },
            ]
          : [{ type: "MasterProducts", id: "LIST" }],
    }),
    getProduct: builder.query({
      query: ({ branchId, productCode }) => ({
        url: `/masters/products/${branchId}/${productCode}`,
      }),
      providesTags: (result, error, { productCode }) => [
        { type: "MasterProducts", id: productCode },
      ],
    }),
    createProduct: builder.mutation({
      query: (body) => ({ url: "/masters/products", method: "POST", data: body }),
      invalidatesTags: [{ type: "MasterProducts", id: "LIST" }],
    }),
    updateProduct: builder.mutation({
      query: ({ branchId, productCode, ...body }) => ({
        url: `/masters/products/${branchId}/${productCode}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: (result, error, { productCode }) => [
        { type: "MasterProducts", id: productCode },
        { type: "MasterProducts", id: "LIST" },
      ],
    }),
    // Groups are branch-wise too ({branchId}/{groupId} on write routes) but
    // the list endpoint returns every branch's groups together (no working
    // branchId filter server-side) - callers filter client-side by branchId.
    getProductGroups: builder.query({
      query: () => ({ url: "/masters/products/groups" }),
      providesTags: (result) =>
        result
          ? [
              ...result.map((g) => ({ type: "MasterProductGroups", id: `${g.branchId}-${g.groupId}` })),
              { type: "MasterProductGroups", id: "LIST" },
            ]
          : [{ type: "MasterProductGroups", id: "LIST" }],
    }),
    createProductGroup: builder.mutation({
      query: (body) => ({ url: "/masters/products/groups", method: "POST", data: body }),
      invalidatesTags: [{ type: "MasterProductGroups", id: "LIST" }],
    }),
    updateProductGroup: builder.mutation({
      query: ({ branchId, groupId, ...body }) => ({
        url: `/masters/products/groups/${branchId}/${groupId}`,
        method: "PUT",
        data: body,
      }),
      invalidatesTags: [{ type: "MasterProductGroups", id: "LIST" }],
    }),
    deleteProductGroup: builder.mutation({
      query: ({ branchId, groupId }) => ({
        url: `/masters/products/groups/${branchId}/${groupId}`,
        method: "DELETE",
      }),
      invalidatesTags: [{ type: "MasterProductGroups", id: "LIST" }],
    }),
    // Cross-branch linking (handoff §9.8/§13.2) - identical shape/semantics
    // to Customer linking, see LinkedProductsPanel.jsx.
    searchProductsAllBranches: builder.query({
      query: (searchQuery) => ({
        url: "/masters/products/search-all-branches",
        params: { query: searchQuery },
      }),
    }),
    getLinkedProducts: builder.query({
      query: ({ branchId, productCode }) => ({
        url: `/masters/products/${branchId}/${productCode}/linked`,
      }),
      providesTags: (result, error, { productCode }) => [
        { type: "MasterProductLinks", id: productCode },
      ],
    }),
    linkProduct: builder.mutation({
      query: ({ branchId, productCode, targetBranchId, targetProductCode }) => ({
        url: `/masters/products/${branchId}/${productCode}/link`,
        method: "POST",
        data: { targetBranchId, targetProductCode },
      }),
      invalidatesTags: (result, error, { productCode }) => [
        { type: "MasterProductLinks", id: productCode },
      ],
    }),
    unlinkProduct: builder.mutation({
      query: ({ branchId, productCode }) => ({
        url: `/masters/products/${branchId}/${productCode}/link`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, { productCode }) => [
        { type: "MasterProductLinks", id: productCode },
      ],
    }),
    // Branch mapping (handoff §13.5) - a different, additional capability
    // from linking above: the product stays ONE row at its home branchId,
    // this just marks which OTHER branches can also see/use it (no second
    // row created). Full-desired-list diff, same convention as guard
    // assignments - send the whole set of target branchIds each time.
    getProductBranchMap: builder.query({
      query: ({ branchId, productCode }) => ({
        url: `/masters/products/${branchId}/${productCode}/branch-map`,
      }),
      providesTags: (result, error, { productCode }) => [
        { type: "MasterProductBranchMap", id: productCode },
      ],
    }),
    setProductBranchMap: builder.mutation({
      query: ({ branchId, productCode, branchIds }) => ({
        url: `/masters/products/${branchId}/${productCode}/branch-map`,
        method: "PUT",
        data: { branchIds },
      }),
      invalidatesTags: (result, error, { productCode }) => [
        { type: "MasterProductBranchMap", id: productCode },
        { type: "MasterProducts", id: "LIST" },
      ],
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetProductsQuery,
  useLazyGetProductsQuery,
  useGetProductQuery,
  useCreateProductMutation,
  useUpdateProductMutation,
  useGetProductGroupsQuery,
  useCreateProductGroupMutation,
  useUpdateProductGroupMutation,
  useDeleteProductGroupMutation,
  useLazySearchProductsAllBranchesQuery,
  useGetLinkedProductsQuery,
  useLinkProductMutation,
  useUnlinkProductMutation,
  useGetProductBranchMapQuery,
  useSetProductBranchMapMutation,
} = productsApi;
